import type { AttachmentDto, ChannelDto } from '@lagerfeuer/shared';
import { ChevronDown, ChevronUp, Paperclip } from 'lucide-react';
import { useState } from 'react';
import { Avatar } from '../../components/Avatar';
import { displayName } from '../../store/selectors';
import { useAppStore } from '../../store/app';
import { MarkdownInline } from '../messages/Markdown';
import { absoluteTime, lastActivity, useStarterMessage } from '../threads/threadUtils';
import { TagChip } from './TagPicker';
import './forum.css';

/** Kopf eines Forenbeitrags im Seitenpanel: Titel, Tags, Startnachricht (einklappbar). */
export function ForumPostHead({ thread, forum }: { thread: ChannelDto; forum: ChannelDto }) {
  const [open, setOpen] = useState(true);
  const starter = useStarterMessage(thread.id);
  const owner = useAppStore((s) => (thread.ownerId ? s.users[thread.ownerId] : undefined));
  const tags = (thread.thread?.appliedTagIds ?? []).map((id) => forum.availableTags?.find((t) => t.id === id)).filter((t) => !!t);
  const msg = starter.data;
  const author = msg?.author ?? owner ?? null;
  return (
    <section className="forum-post-head" data-testid="forum-post-head" aria-label="Startbeitrag">
      <div className="forum-post-head-top">
        <h3 className="forum-post-head-title" data-testid="forum-post-head-title">
          {thread.name}
        </h3>
        <button
          className="icon-btn icon-btn-xs"
          onClick={() => setOpen(!open)}
          aria-expanded={open}
          aria-label={open ? 'Startbeitrag einklappen' : 'Startbeitrag ausklappen'}
          data-testid="forum-starter-toggle"
        >
          {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>
      </div>
      {tags.length > 0 && (
        <div className="forum-tags" aria-label="Tags">
          {tags.map((t) => (
            <TagChip key={t.id} tag={t} small />
          ))}
        </div>
      )}
      {open && (
        <div className="forum-starter" data-testid="forum-starter">
          <div className="forum-starter-meta">
            <Avatar user={author} size={20} />
            <span className="forum-author">{author ? displayName(author) : 'Unbekannt'}</span>
            <time className="small faint" dateTime={msg?.createdAt}>
              {absoluteTime(msg ? Date.parse(msg.createdAt) : lastActivity({ id: thread.id, lastMessageId: null }))}
            </time>
          </div>
          {starter.isLoading && <div className="spinner" />}
          {msg && msg.deleted && <p className="small faint">Die Startnachricht wurde gelöscht.</p>}
          {msg && !msg.deleted && msg.content && (
            <div className="forum-starter-content">
              <MarkdownInline content={msg.content} serverId={thread.serverId} block />
            </div>
          )}
          {msg && msg.attachments.length > 0 && <AttachmentStrip attachments={msg.attachments} />}
        </div>
      )}
    </section>
  );
}

/** Kompakte Anhangsliste (Bild-Vorschauen, sonst Dateilinks). */
export function AttachmentStrip({ attachments }: { attachments: AttachmentDto[] }) {
  return (
    <ul className="forum-attachments" role="list" aria-label="Anhänge">
      {attachments.map((a) => (
        <li key={a.id}>
          <a href={a.url} target="_blank" rel="noopener noreferrer" className="forum-attachment" data-testid="forum-attachment">
            {a.previewUrl && !a.spoiler ? (
              <img src={a.previewUrl} alt={a.description ?? a.filename} loading="lazy" />
            ) : (
              <>
                <Paperclip size={14} aria-hidden="true" />
                <span className="truncate">{a.filename}</span>
              </>
            )}
          </a>
        </li>
      ))}
    </ul>
  );
}
